// src/pages/Futures/FuturesCoinDetails.js
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Skeleton, Drawer, List, ListItem, ListItemText, IconButton, Divider, Chip, Tooltip, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { setSelectedCoin } from '../../store/Slices/FuturesTradingSlice';
import axios from 'axios';

const COLORS = { GREEN: '#0ECB81', RED: '#F6465D', YELLOW: '#F0B90B' };

const FuturesCoinDetails = ({ futuresAssets }) => {
  const dispatch = useDispatch();
  const { selectedCoin } = useSelector((state) => state.futures);
  const [coinData, setCoinData] = useState(null);
  const [livePrice, setLivePrice] = useState(null);
  const [liveChange, setLiveChange] = useState(null);
  const [fundingRate, setFundingRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coinDrawerOpen, setCoinDrawerOpen] = useState(false);
  const [ws, setWs] = useState(null);

  useEffect(() => {
    const fetchCoinData = async () => {
      if (!selectedCoin) return;
      setLoading(true);
      try {
        const symbol = selectedCoin.toUpperCase();
        const [tickerRes, fundingRes] = await Promise.all([
          axios.get('https://fapi.binance.com/fapi/v1/ticker/24hr', { params: { symbol }, timeout: 8000 }),
          axios.get('https://fapi.binance.com/fapi/v1/premiumIndex', { params: { symbol }, timeout: 8000 }),
        ]);
        const ticker = tickerRes.data;
        const price = parseFloat(ticker.lastPrice);
        const change = parseFloat(ticker.priceChangePercent);

        setCoinData({
          symbol: ticker.symbol.replace('USDT', ''),
          price,
          change,
          volume24h: parseFloat(ticker.volume),
          high24h: parseFloat(ticker.highPrice),
          low24h: parseFloat(ticker.lowPrice),
        });
        setLivePrice(price);
        setLiveChange(change);
        setFundingRate(parseFloat(fundingRes.data.lastFundingRate));
      } catch (err) {
        setCoinData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCoinData();
  }, [selectedCoin]);

  useEffect(() => {
    if (!selectedCoin) return;
    if (ws) ws.close();

    const symbol = selectedCoin.toLowerCase();
    const n = new WebSocket(`wss://fstream.binance.com/ws/${symbol}@ticker`);
    n.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d?.c && d?.P) {
          setLivePrice(+d.c);
          setLiveChange(+d.P);
        }
      } catch {}
    };
    setWs(n);
    return () => n.close();
  }, [selectedCoin]);

  const openDrawer = () => setCoinDrawerOpen(true);
  const closeDrawer = () => setCoinDrawerOpen(false);
  const handleSelect = (s) => { dispatch(setSelectedCoin(`${s}USDT`)); closeDrawer(); };

  if (!selectedCoin) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body1" sx={{ cursor: 'pointer' }} onClick={openDrawer}>
          Select a futures coin
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="text" width={160} height={32} />
        <Skeleton variant="text" width={140} height={32} />
      </Box>
    );
  }

  const priceColor = (liveChange ?? 0) >= 0 ? COLORS.GREEN : COLORS.RED;

  return (
    <>
      <Paper sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={openDrawer}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedCoin.replace('USDT', '')}/USDT
          </Typography>
          <Tooltip title="Change Pair">
            <IconButton size="small" color="inherit">
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="h6" sx={{ color: priceColor, fontWeight: 700 }}>
          {livePrice?.toFixed(2) || '--'} USDT
        </Typography>

        <Chip
          size="small"
          label={`${(liveChange ?? 0).toFixed(2)}%`}
          sx={{
            bgcolor: (liveChange ?? 0) >= 0 ? 'rgba(14,203,129,0.15)' : 'rgba(246,70,93,0.15)',
            color: priceColor,
            fontWeight: 600,
          }}
        />

        {coinData && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              24h Vol: {(coinData.volume24h / 1000).toFixed(2)}K
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              H: {coinData.high24h?.toFixed(2)} / L: {coinData.low24h?.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Funding: {(fundingRate * 100 || 0).toFixed(4)}%
            </Typography>
          </>
        )}
      </Paper>

      <Drawer anchor="left" open={coinDrawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Select Futures Coin</Typography>
            <IconButton onClick={closeDrawer}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List>
            {futuresAssets?.length ? futuresAssets.map((a, i) => (
              <ListItem key={`${a.symbol}-${i}`} button onClick={() => handleSelect(a.symbol)}>
                <ListItemText primary={`${a.symbol}USDT`} secondary={a.name} />
              </ListItem>
            )) : (
              <Typography sx={{ color: 'text.secondary', p: 1 }}>No assets</Typography>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default FuturesCoinDetails;
